/**
 * ia-video – plays desktop or mobile video based on viewport. Centered play button.
 * Use data-video-wrap="desktop" and data-video-wrap="mobile" for the two video wrappers.
 */
(function () {
  function getBreakpoint(el) {
    return parseInt(el.getAttribute('data-breakpoint') || '768', 10);
  }

  function getVisibleVideo(el, breakpoint) {
    var desktopWrap = el.querySelector('[data-video-wrap="desktop"]');
    var mobileWrap = el.querySelector('[data-video-wrap="mobile"]');
    var isDesktop = window.matchMedia('(min-width: ' + breakpoint + 'px)').matches;
    var wrap = isDesktop ? desktopWrap : mobileWrap;
    return wrap ? wrap.querySelector('video') : null;
  }

  class IaVideo extends HTMLElement {
    connectedCallback() {
      this._overlay = this.querySelector('[data-overlay]');
      this._poster = this.querySelector('[data-poster]');
      this._playBtn = this.querySelector('[data-action="play"]');
      this._inner = this.querySelector('[data-video-inner]');
      this._breakpoint = getBreakpoint(this);
      this._autoplay = this.hasAttribute('data-autoplay');

      if (!this._overlay || !this._playBtn) return;

      this._playBtn.addEventListener('click', this._handlePlay.bind(this));
      this._boundVideoAreaClick = this._handleVideoAreaClick.bind(this);
      if (this._inner) {
        this._inner.addEventListener('click', this._boundVideoAreaClick);
      }
      this._boundOnPause = this._onPause.bind(this);
      this._boundOnEnded = this._onEnded.bind(this);
      this._boundOnResize = this._onResize.bind(this);
      window.addEventListener('resize', this._boundOnResize);

      if (this._autoplay) {
        this._setupAutoplay();
      }
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this._boundOnResize);
      if (this._inner && this._boundVideoAreaClick) {
        this._inner.removeEventListener('click', this._boundVideoAreaClick);
      }
      var video = this._playingVideo;
      if (video) {
        video.removeEventListener('pause', this._boundOnPause);
        video.removeEventListener('ended', this._boundOnEnded);
      }
      if (this._observer) {
        this._observer.disconnect();
      }
    }

    _setupAutoplay() {
      this._observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          var video = getVisibleVideo(this, this._breakpoint);
          if (!video) return;
          if (entry.isIntersecting) {
            video.muted = true;
            video.play().then(() => {
              this._overlay.hidden = true;
              if (this._poster) this._poster.hidden = true;
              this.setAttribute('data-playing', '');
              this._playingVideo = video;
            }).catch(() => {});
          } else {
            video.pause();
            if (this._playingVideo === video) {
              this._playingVideo = null;
              this._overlay.hidden = false;
              this.removeAttribute('data-playing');
            }
          }
        });
      }, { threshold: 0.25 });

      this._observer.observe(this);
    }

    _handleVideoAreaClick(e) {
      if (e.target.closest('[data-action="play"]')) return;
      if (!this._overlay.hidden || !this._playingVideo) return;
      this._playingVideo.pause();
      this._showOverlay();
    }

    _handlePlay() {
      var video = getVisibleVideo(this, this._breakpoint);
      if (!video) return;

      this._playingVideo = video;
      video.addEventListener('pause', this._boundOnPause, { once: false });
      video.addEventListener('ended', this._boundOnEnded, { once: false });
      video.play();
      this._overlay.hidden = true;
      if (this._poster) this._poster.hidden = true;
      this.setAttribute('data-playing', '');
    }

    _onPause() {
      if (this._playingVideo && this._playingVideo.paused) {
        this._showOverlay();
      }
    }

    _onEnded() {
      this._showOverlay();
    }

    _showOverlay() {
      var video = this._playingVideo;
      if (video) {
        video.removeEventListener('pause', this._boundOnPause);
        video.removeEventListener('ended', this._boundOnEnded);
        this._playingVideo = null;
        if (this._poster) {
          var t = video.currentTime;
          var d = video.duration;
          var atStart = t < 1;
          var atEnd = d > 0 && t >= d - 0.5;
          this._poster.hidden = !atStart && !atEnd;
        }
      } else if (this._poster) {
        this._poster.hidden = false;
      }
      this._overlay.hidden = false;
      this.removeAttribute('data-playing');
    }

    _onResize() {
      var video = this._playingVideo;
      if (!video) return;
      var nowVisible = getVisibleVideo(this, this._breakpoint);
      if (nowVisible !== video) {
        video.pause();
        this._showOverlay();
      }
    }
  }

  if (!customElements.get('ia-video')) {
    customElements.define('ia-video', IaVideo);
  }
})();